// Third party
import React from 'react';

// Types
import User from '@/types/user';

// Components
import LinkLogo from '@/views/components/link/LinkLogo';
import { CloseButton, Button } from '../../../components/buttons';
import LinkHeader from '@/views/components/link/LinkHeader';

// Utils
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import useScreenSize from '@/hooks/useScreenSize';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import useThemeVersion from '@/hooks/useThemeVersion';

type LinkProps = {
  user: User;
  handleProposalClose: () => void;
  refreshTemplate: () => void;
  goBackToComparison: () => void;
};

const LinkTopHeader = ({ user, handleProposalClose, refreshTemplate, goBackToComparison }: LinkProps) => {
  const { isDesktop } = useScreenSize();
  const { isLightVersion } = useThemeVersion();

  // Legacy Mode include users on the dark mode or users who don't have accessibility mode
  const nonAccessibleView = !userHasPermissionEnabled(user, PERMISSIONS.PROPOSAL_ACCESSIBILITY) || !isLightVersion;

  const getLinkTopBarLeftItems = () => {
    const componentsArray: React.ReactNode[] = [];

    if (isDesktop || nonAccessibleView) {
      componentsArray.push(
        <Button
          key="ProposalTopBarNavigation.BackButton"
          onClick={goBackToComparison}
          className="top-bar-button"
          aria-label={formatMessage('Proposal.ReturnToComparison')}
          icon="arrow-thin-left"
          dark
        >
          <FormattedMessage id="Common.Back" />
        </Button>,
      );

      if (import.meta.env.DEV) {
        componentsArray.push(
          <Button
            key="ProposalTopBarNavigation.Reload"
            className="top-bar-button"
            onClick={refreshTemplate}
            aria-label={formatMessage('Proposal.Reload')}
            icon="spin-arrow"
            dark
          >
            <FormattedMessage id="Proposal.Reload" />
          </Button>,
        );
      }
    } else {
      componentsArray.push(
        <Button
          key="ProposalTopBarNavigation.BackButton"
          onClick={goBackToComparison}
          className="top-bar-button"
          aria-label={formatMessage('Proposal.ReturnToComparison')}
          icon="arrow-thin-left"
          dark
        />,
      );
    }

    return componentsArray;
  };

  const getLinkTopBarRightItems = () => {
    if (isDesktop || nonAccessibleView) {
      return <CloseButton key="ProposalTopBarNavigation.CloseButton" onClick={handleProposalClose} className="top-bar-button" withText dark />;
    }
    return undefined;
  };

  return (
    <LinkHeader
      user={user}
      leftItems={getLinkTopBarLeftItems()}
      middleItems={<LinkLogo key="ProposalTopBarNavigation.LinkLogo" user={user} />}
      rightItems={getLinkTopBarRightItems()}
    />
  );
};

export default LinkTopHeader;
